import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

import { webPublicRuntimeEnv, webServerRuntimeEnv } from "./runtime.generated";

export const env = createEnv({
  server: {
    // Service-role key — only used in Edge route handlers, never sent to the
    // browser. Intentionally kept out of the NEXT_PUBLIC_ namespace.
    SUPABASE_SERVICE_ROLE_KEY: z.string().optional(),
  },
  client: {
    NEXT_PUBLIC_API_URL: z.url().default("http://localhost:8080/api/v1"),
    NEXT_PUBLIC_SUPABASE_URL: z.url(),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),
    NEXT_PUBLIC_GTM_ID: z.string().optional(),
    NEXT_PUBLIC_GA_MEASUREMENT_ID: z.string().default("G-3BBENBX8DP"),
    NEXT_PUBLIC_SENTRY_DSN: z.url().optional(),
    // E.164 number (no `+`, no spaces) used only for paid-report checkout
    // recovery handoff. Global support now routes to support@ishvaram.com.
    NEXT_PUBLIC_SUPPORT_WHATSAPP: z.string().optional(),
    NEXT_PUBLIC_META_PIXEL_ID: z.string().optional(),
    // Cloudflare Turnstile site key (public). When unset, the Turnstile gate
    // renders nothing and no token is attached — the feature is dark. Set it to
    // activate the invisible anti-bot widget on checkout + chat.
    NEXT_PUBLIC_TURNSTILE_SITEKEY: z.string().optional(),
    // Streaming flag. Absent or any value other than "0"/"false" → streaming ON.
    // Set to "0" to revert to unary non-streaming path (incident rollback).
    NEXT_PUBLIC_CHAT_STREAMING_ENABLED: z.string().optional(),
    NEXT_PUBLIC_CHAT_USE_VERCEL_SDK: z.string().optional(),
    // Semantic offer-moment flag. Ships DARK (unset = off). Set to "1" to
    // enable surfacing the mid-chat nudge early when the AI lands a specific
    // planet+house insight tied to the session's concern. Zero new offer
    // components — only changes TIMING of the existing ChatMidChatNudge.
    NEXT_PUBLIC_CHAT_OFFER_MOMENT_ENABLED: z.string().optional(),
    // #4 credit-ladder honest next-step. Unset/"0" = dark (paywall unchanged);
    // "1" surfaces the chart-matched remedy report as the ₹29 seeker's next step
    // at its real price via the existing checkout (no backend ₹29-credit rule).
    NEXT_PUBLIC_CHAT_CREDIT_LADDER_ENABLED: z.string().optional(),
    // WS-D delivery-moment offer. Default ON — unset/"1" renders the ONE matched
    // second-report offer under the purchase-success confirmation, at its normal
    // price via the existing report checkout. Set "0"/"false" to kill.
    NEXT_PUBLIC_POST_PURCHASE_OFFER_ENABLED: z.string().optional(),
    // WS-K resolution_7d package. Unset/"0" = dark (paywall + ladder unchanged);
    // "1" surfaces the ₹199 remedy-report + 7-din guided-session package at the
    // governed chat moments, arm-gated by the resolution_package_v1 experiment.
    NEXT_PUBLIC_RESOLUTION_PACKAGE_ENABLED: z.string().optional(),
    // Membership checkout dark-launch flag. Backend also requires
    // MEMBERSHIP_SUBSCRIPTIONS_ENABLED and Razorpay plan IDs before money can move.
    NEXT_PUBLIC_MEMBERSHIP_ENABLED: z.string().optional(),
    // Aaj Ka Panchang web-push (ADR-0027 slice 3). VAPID applicationServerKey
    // (base64url). Unset → the /my-panchang push opt-in renders nothing (dark).
    NEXT_PUBLIC_VAPID_PUBLIC_KEY: z.string().optional(),
    // Lever 2 value-moment capture on saved kundali; explicit-consent capture
    // only, no lifecycle/upsell send.
    NEXT_PUBLIC_VALUE_MOMENT_CAPTURE: z.string().optional(),
  },
  // Most vars are NEXT_PUBLIC (static export); server-only secrets use the
  // server block above and are stripped from the client bundle automatically.
  runtimeEnv: {
    ...webServerRuntimeEnv,
    ...webPublicRuntimeEnv,
    NEXT_PUBLIC_GA_MEASUREMENT_ID:
      webPublicRuntimeEnv.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? "G-3BBENBX8DP",
  },
  skipValidation: process.env.SKIP_ENV_VALIDATION === "true",
});
